import {
  getFestivals,
  getAdminFestivals,
  getFestivalById,
  getFestivalsForDropdown,
  getFeaturedFestivals,
  createFestival,
  editFestival,
  deleteFestival,
  toggleFestivalStatus,
  getProductsByFestival,
  createFestivalDashboardAccess,
  resetFestivalDashboardPassword,
  removeFestivalDashboardAccess,
  getFestivalDetails,
  updateFestivalDetails,
  getFestivalPublicDetails,
  getFestivalByName,
} from "./asyncThunks";

const parseJsonField = (value, fallback, festivalName, fieldName) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error(
        `Error parsing ${fieldName} for festival`,
        festivalName,
        e,
      );
      return fallback;
    }
  }
  return value || fallback;
};

const parseFestival = (festival) => {
  const events = parseJsonField(festival.events, [], festival.name, "events");
  const stats = parseJsonField(festival.stats, {}, festival.name, "stats");

  return {
    ...festival,
    events,
    stats,
    products: festival.numOfProducts || festival.products || 0,
  };
};

export const getFestivalsBuilder = (builder) => {
  builder.addCase(getFestivals.pending, (state) => {
    state.festivals.loading = true;
    state.festivals.error = null;
  });
  builder.addCase(getFestivals.fulfilled, (state, { payload }) => {
    const { data, pagination, maxReached } = payload;
    state.festivals.data = data.map(parseFestival);
    state.festivals.pagination = pagination;
    state.festivals.maxReached = maxReached;
    state.festivals.loading = false;
  });
  builder.addCase(getFestivals.rejected, (state, action) => {
    state.festivals.loading = false;
    state.festivals.error = action.error.message;
  });
};

export const getAdminFestivalsBuilder = (builder) => {
  builder.addCase(getAdminFestivals.pending, (state) => {
    state.festivals.loading = true;
    state.festivals.error = null;
  });
  builder.addCase(getAdminFestivals.fulfilled, (state, { payload }) => {
    const { data, pagination, maxReached } = payload;
    state.festivals.data = data.map(parseFestival);
    state.festivals.pagination = pagination;
    state.festivals.maxReached = maxReached;
    state.festivals.loading = false;
  });
  builder.addCase(getAdminFestivals.rejected, (state, action) => {
    state.festivals.loading = false;
    state.festivals.error = action.error.message;
  });
};

export const getFestivalByIdBuilder = (builder) => {
  builder.addCase(getFestivalById.pending, (state) => {
    state.festivalById.loading = true;
    state.festivalById.error = null;
  });
  builder.addCase(getFestivalById.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivalById.data = data;
    state.festivalById.loading = false;
  });
  builder.addCase(getFestivalById.rejected, (state, action) => {
    state.festivalById.loading = false;
    state.festivalById.error = action.error.message;
  });
};

export const getFestivalsForDropdownBuilder = (builder) => {
  builder.addCase(getFestivalsForDropdown.pending, (state) => {
    state.festivalsDropdown.loading = true;
    state.festivalsDropdown.error = null;
  });
  builder.addCase(getFestivalsForDropdown.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivalsDropdown.data = data;
    state.festivalsDropdown.loading = false;
  });
  builder.addCase(getFestivalsForDropdown.rejected, (state, action) => {
    state.festivalsDropdown.loading = false;
    state.festivalsDropdown.error = action.error.message;
  });
};

export const getFeaturedFestivalsBuilder = (builder) => {
  builder.addCase(getFeaturedFestivals.pending, (state) => {
    state.featuredFestivals.loading = true;
    state.featuredFestivals.error = null;
  });
  builder.addCase(getFeaturedFestivals.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.featuredFestivals.data = data;
    state.featuredFestivals.loading = false;
  });
  builder.addCase(getFeaturedFestivals.rejected, (state, action) => {
    state.featuredFestivals.loading = false;
    state.featuredFestivals.error = action.error.message;
  });
};

export const createFestivalBuilder = (builder) => {
  builder.addCase(createFestival.pending, (state) => {
    state.festivals.createLoading = true;
    state.festivals.error = null;
  });
  builder.addCase(createFestival.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivals.data = [data, ...state.festivals.data];
    state.festivals.createLoading = false;
  });
  builder.addCase(createFestival.rejected, (state, action) => {
    state.festivals.createLoading = false;
    state.festivals.error = action.error.message;
  });
};

export const editFestivalBuilder = (builder) => {
  builder.addCase(editFestival.pending, (state) => {
    state.festivals.editLoading = true;
    state.festivals.error = null;
  });
  builder.addCase(editFestival.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivals.data = state.festivals.data.map((festival) =>
      festival.id === data.id ? data : festival,
    );
    if (state.festivalById.data && state.festivalById.data.id === data.id) {
      state.festivalById.data = data;
    }
    state.festivalsDropdown.data = state.festivalsDropdown.data.map(
      (festival) =>
        festival.id === data.id
          ? { id: data.id, name: data.name }
          : festival,
    );
    state.festivals.editLoading = false;
  });
  builder.addCase(editFestival.rejected, (state, action) => {
    state.festivals.editLoading = false;
    state.festivals.error = action.error.message;
  });
};

export const deleteFestivalBuilder = (builder) => {
  builder.addCase(deleteFestival.pending, (state) => {
    state.festivals.deleteLoading = true;
    state.festivals.error = null;
  });
  builder.addCase(deleteFestival.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivals.data = state.festivals.data.filter(
      (festival) => festival.id !== data.id,
    );
    state.festivalsDropdown.data = state.festivalsDropdown.data.filter(
      (festival) => festival.id !== data.id,
    );
    if (state.festivalById.data && state.festivalById.data.id === data.id) {
      state.festivalById.data = null;
    }
    state.festivals.deleteLoading = false;
  });
  builder.addCase(deleteFestival.rejected, (state, action) => {
    state.festivals.deleteLoading = false;
    state.festivals.error = action.error.message;
  });
};

export const toggleFestivalStatusBuilder = (builder) => {
  builder.addCase(toggleFestivalStatus.pending, (state) => {
    state.festivals.toggleLoading = true;
    state.festivals.error = null;
  });
  builder.addCase(toggleFestivalStatus.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivals.data = state.festivals.data.map((festival) =>
      festival.id === data.id ? data : festival,
    );
    if (state.festivalById.data && state.festivalById.data.id === data.id) {
      state.festivalById.data = data;
    }
    state.festivals.toggleLoading = false;
  });
  builder.addCase(toggleFestivalStatus.rejected, (state, action) => {
    state.festivals.toggleLoading = false;
    state.festivals.error = action.error.message;
  });
};

// DASHBOARD PRISTUP

export const createFestivalDashboardAccessBuilder = (builder) => {
  builder.addCase(createFestivalDashboardAccess.pending, (state) => {
    state.dashboardAccess.createLoading = true;
    state.dashboardAccess.error = null;
  });
  builder.addCase(
    createFestivalDashboardAccess.fulfilled,
    (state, { payload }) => {
      const { data } = payload;

      state.festivals.data = state.festivals.data.map((festival) => {
        if (festival.id === data.festivalId) {
          return {
            ...festival,
            adminCredentials: {
              username: data.username,
              hasAccess: true,
            },
          };
        }
        return festival;
      });

      if (
        state.festivalById.data &&
        state.festivalById.data.id === data.festivalId
      ) {
        state.festivalById.data = {
          ...state.festivalById.data,
          adminCredentials: {
            username: data.username,
            hasAccess: true,
          },
        };
      }

      state.dashboardAccess.createLoading = false;
      state.dashboardAccess.lastCreated = data;
    },
  );
  builder.addCase(
    createFestivalDashboardAccess.rejected,
    (state, action) => {
      state.dashboardAccess.createLoading = false;
      state.dashboardAccess.error = action.error.message;
    },
  );
};

export const resetFestivalDashboardPasswordBuilder = (builder) => {
  builder.addCase(resetFestivalDashboardPassword.pending, (state) => {
    state.dashboardAccess.resetLoading = true;
    state.dashboardAccess.error = null;
  });
  builder.addCase(
    resetFestivalDashboardPassword.fulfilled,
    (state, { payload }) => {
      const { data } = payload;
      state.dashboardAccess.resetLoading = false;
      state.dashboardAccess.lastReset = {
        festivalId: data.festivalId,
        username: data.username,
        timestamp: new Date().toISOString(),
      };
    },
  );
  builder.addCase(
    resetFestivalDashboardPassword.rejected,
    (state, action) => {
      state.dashboardAccess.resetLoading = false;
      state.dashboardAccess.error = action.error.message;
    },
  );
};

export const removeFestivalDashboardAccessBuilder = (builder) => {
  builder.addCase(removeFestivalDashboardAccess.pending, (state) => {
    state.dashboardAccess.removeLoading = true;
    state.dashboardAccess.error = null;
  });
  builder.addCase(
    removeFestivalDashboardAccess.fulfilled,
    (state, { payload }) => {
      const { data } = payload;

      state.festivals.data = state.festivals.data.map((festival) => {
        if (festival.id === data.festivalId) {
          return {
            ...festival,
            adminCredentials: null,
          };
        }
        return festival;
      });

      if (
        state.festivalById.data &&
        state.festivalById.data.id === data.festivalId
      ) {
        state.festivalById.data = {
          ...state.festivalById.data,
          adminCredentials: null,
        };
      }

      state.dashboardAccess.removeLoading = false;
      state.dashboardAccess.lastRemoved = data;
    },
  );
  builder.addCase(
    removeFestivalDashboardAccess.rejected,
    (state, action) => {
      state.dashboardAccess.removeLoading = false;
      state.dashboardAccess.error = action.error.message;
    },
  );
};

export const getProductsByFestivalBuilder = (builder) => {
  builder.addCase(getProductsByFestival.pending, (state) => {
    state.festivalProducts.loading = true;
    state.festivalProducts.error = null;
  });
  builder.addCase(getProductsByFestival.fulfilled, (state, { payload }) => {
    const { data, total, page, perPage, maxReached, festival } = payload;
    state.festivalProducts.data = data;
    state.festivalProducts.total = total;
    state.festivalProducts.page = page;
    state.festivalProducts.perPage = perPage;
    state.festivalProducts.maxReached = maxReached;
    state.festivalProducts.festival = festival;
    state.festivalProducts.loading = false;
  });
  builder.addCase(getProductsByFestival.rejected, (state, action) => {
    state.festivalProducts.loading = false;
    state.festivalProducts.error = action.error.message;
  });
};

export const getFestivalDetailsBuilder = (builder) => {
  builder.addCase(getFestivalDetails.pending, (state) => {
    state.festivalDetails.loading = true;
    state.festivalDetails.error = null;
  });
  builder.addCase(getFestivalDetails.fulfilled, (state, { payload }) => {
    state.festivalDetails.data = payload.data || payload;
    state.festivalDetails.loading = false;
  });
  builder.addCase(getFestivalDetails.rejected, (state, action) => {
    state.festivalDetails.loading = false;
    state.festivalDetails.error = action.payload || action.error.message;
  });
};

export const updateFestivalDetailsBuilder = (builder) => {
  builder.addCase(updateFestivalDetails.pending, (state) => {
    state.festivalDetails.updateLoading = true;
    state.festivalDetails.error = null;
  });
  builder.addCase(updateFestivalDetails.fulfilled, (state, { payload }) => {
    const updatedData = payload.data || payload;
    state.festivalDetails.data = updatedData;
    state.festivalDetails.updateLoading = false;

    if (state.festivals.data.length > 0) {
      state.festivals.data = state.festivals.data.map((festival) =>
        festival.id === updatedData.id
          ? { ...festival, ...updatedData }
          : festival,
      );
    }

    if (
      state.festivalById.data &&
      state.festivalById.data.id === updatedData.id
    ) {
      state.festivalById.data = {
        ...state.festivalById.data,
        ...updatedData,
      };
    }
  });
  builder.addCase(updateFestivalDetails.rejected, (state, action) => {
    state.festivalDetails.updateLoading = false;
    state.festivalDetails.error = action.payload || action.error.message;
  });
};

export const getFestivalPublicDetailsBuilder = (builder) => {
  builder.addCase(getFestivalPublicDetails.pending, (state) => {
    state.publicFestivalDetails.loading = true;
    state.publicFestivalDetails.error = null;
  });
  builder.addCase(
    getFestivalPublicDetails.fulfilled,
    (state, { payload }) => {
      const { data } = payload;

      const parsedData = { ...data };
      parsedData.events = parseJsonField(data.events, [], data.name, "events");
      parsedData.stats = parseJsonField(data.stats, {}, data.name, "stats");

      if (
        data.fbUrl ||
        data.instaUrl ||
        data.ytUrl ||
        data.spotifyUrl ||
        data.appleMusicUrl ||
        data.soundcloudUrl ||
        data.xUrl ||
        data.websiteUrl
      ) {
        parsedData.socialLinks = {
          facebook: data.fbUrl,
          instagram: data.instaUrl,
          youtube: data.ytUrl,
          spotify: data.spotifyUrl,
          apple: data.appleMusicUrl,
          soundcloud: data.soundcloudUrl,
          twitter: data.xUrl,
          website: data.websiteUrl,
        };
      }

      if (data.festivalImages && Array.isArray(data.festivalImages)) {
        parsedData.gallery = data.festivalImages;
      }

      state.publicFestivalDetails.data = parsedData;
      state.publicFestivalDetails.loading = false;
    },
  );
  builder.addCase(getFestivalPublicDetails.rejected, (state, action) => {
    state.publicFestivalDetails.loading = false;
    state.publicFestivalDetails.error = action.error.message;
  });
};

export const getFestivalByNameBuilder = (builder) => {
  builder.addCase(getFestivalByName.pending, (state) => {
    state.festivalByName.loading = true;
    state.festivalByName.error = null;
  });
  builder.addCase(getFestivalByName.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.festivalByName.data = data;
    state.festivalByName.loading = false;
  });
  builder.addCase(getFestivalByName.rejected, (state, action) => {
    state.festivalByName.loading = false;
    state.festivalByName.error = action.error.message;
  });
};
